import { useCallback, useEffect, useMemo, useRef, useState } from 'react'
import { createPortal } from 'react-dom'
import { useSearchParams } from 'react-router-dom'
import { useRailSlot } from '../layout/railSlot'
import { useViewCtx } from '../context/ViewContext'
import { useOrg } from '../org'
import { ContextBlock } from '../layout/ContextBlock'
import { OverlaysBlock } from '../layout/OverlaysBlock'
import { useOverlays } from '../map/useOverlays'
import { useQuakeCard } from '../map/useQuakeCard'
import { usePointCard } from '../map/usePointCard'
import { PointCard } from '../map/PointCard'
import { EarthquakeCard } from '../models/EarthquakeCard'
import { saveViewState } from '../settings/saveViewState'
import { useVesselFocus } from '../map/useVesselFocus'
import { EezCard } from '../map/EezCard'
import { useEezCard } from '../map/useEezCard'
import { useDistrictsLayer } from '../map/useDistrictsLayer'
import { useSubstrateLayer } from '../map/useSubstrateLayer'
import { useNavwarnLayer } from '../map/useNavwarnLayer'
import { substrateAt } from '../map/substrateLayer'
import { useBansLayer } from '../map/useBansLayer'
import { useEezLayer } from '../map/useEezLayer'
import { useIsobathTool } from '../map/useIsobathTool'
import { ExportViewButton } from '../map/ExportViewButton'
import { layerStateOf } from '../map/exportReady'
import { ShareLinkButton } from '../map/ShareLinkButton'
import { useExportInfo } from '../map/useExportInfo'
import { useFleetLayer } from '../map/useFleetLayer'
import { Icon } from '../brand/Icon'
import { useQuery } from '@tanstack/react-query'
import { cabinetApi } from '../api'
import { MapCanvas, type MapCanvasHandle } from '../map/MapCanvas'
import { useTemperatureTools } from '../map/useTemperatureTools'
import { useWaveIso } from '../map/useWaveIso'
import { MapLegendPanel } from './MapLegendPanel'
import { withSectionLayers } from '../map/overlayLayers'
import { arrowScale, arrowStepDeg, arrowsNotice, type ArrowsState } from '../models/arrows'
import { layerLabel, useT } from '../i18n'
import { serializeFleet } from '../models/fleetSegments'
import { LoadDataButton } from '../map/LoadDataButton'
import { ManifestModal } from '../map/ManifestModal'
import { useLoadData } from '../map/useLoadData'
import { useTilePalette } from '../map/useTilePalette'
import { sharedTileUrl, bathymetryTileUrl } from '../map/tileUrl'
import { parseMapState, mapStateToQuery, depthDisabled, type MapState } from '../mapState'
import { clampLayer } from '../hydrology'
import { readinessAction, bustedTileUrl } from '../map/readiness'
import { roundView, sameView, viewOnEnter } from '../map/mapView'
import { RegionForbiddenPlate } from '../species/RegionForbiddenPlate'
import { viewToOpen } from '../species/regionHome'
import { useRegionScope } from '../species/useRegionScope'
import { regionAt } from '../species/regionFilter'
import { LayerPicker } from './LayerPicker'
import { ScalePanel } from './ScalePanel'
import { MapPanel } from '../map/MapPanel'

const today = () => new Date().toISOString().slice(0, 10)

export function MapsPage() {
  const t = useT()
  const [searchParams, setSearchParams] = useSearchParams()
  const mapRef = useRef<MapCanvasHandle>(null)
  // Актуальный шаблон тайлов текущего слоя — чтобы устаревшая задача (сменили слой
  // во время генерации) не нарисовала свои тайлы поверх нового слоя.
  const currentTileUrlRef = useRef('')
  const [layerShown, setLayerShown] = useState(false)
  // Режим прогрессивного докрашивания: данные есть, тайлы генерируются → серые
  // плейсхолдеры периодически перезапрашиваются и сменяются цветными.
  const [progressive, setProgressive] = useState(false)
  // Версия отрендеренных тайлов (availability.tiles_version) — часть URL тайла.
  // Без неё браузер держит старую картинку сутки (max-age=86400, URL прежний).
  const [tilesVersion, setTilesVersion] = useState<string | null>(null)
  const railSlot = useRailSlot()

  const { data: catalogData } = useQuery({
    queryKey: ['cabinet', 'layers'],
    queryFn: () => cabinetApi.layers(),
  })
  const catalog = useMemo(() => catalogData?.layers ?? [], [catalogData])

  // Районы, разрешённые доменом и правами организации: карта открывается
  // рамкой первого из них, а пустое пересечение говорит плашкой, а не пустотой.
  const regionScope = useRegionScope()

  // Дата·глубина·вид — из общего контекста (блок «Контекст» над разделами). Зеркалим
  // их в локальный state, чтобы все существующие чтения state.date/state.depth
  // работали без изменений; страница остаётся единственным писателем URL этих ключей
  // (mapStateToQuery), питая их значениями контекста.
  const {
    date: ctxDate,
    depth: ctxDepth,
    species: ctxSpecies,
    setDepthApplicable,
    overlays: ctxOverlays,
    fleet,
    showUncategorized,
    flags,
    setFlagCounts,
    focusVessel,
    isobathStep,
    waveOverlay,
    waveStep,
    isotherm: ctxIsotherm,
    isothermSurface: ctxIsothermSurface,
    isothermSpread: ctxIsothermSpread,
    fronts: ctxFronts,
    frontsUnit: ctxFrontsUnit,
    recallMapView,
    rememberMapView,
  } = useViewCtx()

  // Вид карты страница держит внутри своего MapState, но помнит его общий контекст:
  // ключи из ссылки сильнее, недостающие берём из памяти сессии — иначе возврат из
  // другого раздела (голый путь без query) открывал бы карту на дефолтном центре.
  const [state, setState] = useState<MapState>(() => {
    const parsed = parseMapState(searchParams)
    return { ...parsed, ...viewOnEnter(parsed, recallMapView()), date: parsed.date || today() }
  })

  const { activeOrgId } = useOrg()
  // Контекст → локальное состояние, из которого пишется URL. Три списка подряд
  // (сравнение, присвоение, зависимости) обязаны совпадать: значение, забытое
  // хоть в одном, в `state` не приезжает НИКОГДА, и адрес вечно несёт то, что
  // было при открытии страницы. Так молчали изобаты и выбор флагов.
  // Сторож совпадения списков — models/urlSyncDeps.test.ts.
  useEffect(() => {
    setState(s =>
      s.date === ctxDate &&
      s.depth === ctxDepth &&
      s.species === ctxSpecies &&
      s.overlays === ctxOverlays &&
      s.fleet === fleet &&
      s.waves === waveOverlay &&
      s.waveStep === waveStep &&
      s.showUncat === showUncategorized &&
      s.flags === flags &&
      s.isobath === isobathStep &&
      s.isotherm === ctxIsotherm &&
      s.isothermSurface === ctxIsothermSurface &&
      s.isothermSpread === ctxIsothermSpread &&
      s.fronts === ctxFronts &&
      s.frontsUnit === ctxFrontsUnit
        ? s
        : {
            ...s,
            date: ctxDate,
            depth: ctxDepth,
            species: ctxSpecies,
            overlays: ctxOverlays,
            fleet,
            waves: waveOverlay,
            waveStep,
            showUncat: showUncategorized,
            flags,
            isobath: isobathStep,
            isotherm: ctxIsotherm,
            isothermSurface: ctxIsothermSurface,
            isothermSpread: ctxIsothermSpread,
            fronts: ctxFronts,
            frontsUnit: ctxFrontsUnit,
          },
    )
  }, [ctxDate, ctxDepth, ctxSpecies, ctxOverlays, fleet, waveOverlay, waveStep, showUncategorized, flags, isobathStep, ctxIsotherm, ctxIsothermSurface, ctxIsothermSpread, ctxFronts, ctxFrontsUnit])

  const openWith = useMemo(
    () =>
      viewToOpen(
        { lng: state.lng, lat: state.lat, zoom: state.zoom },
        regionScope.home,
        regionScope.known,
      ),
    [state.lng, state.lat, state.zoom, regionScope.home, regionScope.known],
  )

  // синхронизировать состояние в URL (и регион — в память разделов)
  useEffect(() => {
    rememberMapView({ lng: state.lng, lat: state.lat, zoom: state.zoom })
    setSearchParams(mapStateToQuery(state), { replace: true })
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [state])

  // Счётчик перерисовки слоя флота: фоновое обновление позиций из MarineTraffic
  // дописало точки в базу, а вьюпорт при этом не менялся — без своего ключа
  // слой не узнал бы, что данные обновились.
  const [fleetNonce, setFleetNonce] = useState(0)

  // Счётчик завершившихся докачек: инструменты наложения (изотермы, фронты,
  // волнение) держат поле, взятое один раз на вьюпорт, и без этого сигнала после
  // «Обновить» показывали бы до-докачечную сетку (см. overlayGridKey).
  const [loadNonce, setLoadNonce] = useState(0)

  const { state: loadState, progress, cardProgress, manifest, error, run, confirm, cancel } = useLoadData(
    tileUrl => {
      // Данные докачаны — сетку инструментов перечитать в любом случае, даже если
      // тайлы ниже относятся к уже смененному слою: качали их по вьюпорту, а не
      // по слою, и свежести это не отменяет.
      setLoadNonce(n => n + 1)
      // Задача генерации завершилась → показать цветной слой, выйти из серого режима.
      // Игнорируем устаревшую задачу, если пользователь уже сменил слой.
      if (tileUrl !== currentTileUrlRef.current) return
      mapRef.current?.showLayer(tileUrl)
      setLayerShown(true)
      setProgressive(false)
    },
    // Фоновое обновление позиций судов закончилось — перерисовать слой флота.
    () => setFleetNonce(n => n + 1),
  )

  // Слой из адреса может быть кабинету не отдан (ссылка из закладки, а среда
  // закрыта признаком организации): открываем разрешённый, а не пустую карту.
  useEffect(() => {
    const allowed = clampLayer(state.layer, catalog)
    if (allowed !== state.layer) setState(s => ({ ...s, layer: allowed }))
  }, [state.layer, catalog])

  const activeLayerSpec = catalog.find(l => l.id === state.layer)
  const depthOff = depthDisabled(state.layer, catalog)

  // Блок «Контекст» гасит глубиномер для слоёв без глубины (SST/батиметрия/ssha/оверлеи).
  useEffect(() => {
    setDepthApplicable(!depthOff)
  }, [depthOff, setDepthApplicable])

  // Края цветовой шкалы для чисел на легенде. Ключуем на версию тайлов — при
  // перегенерации шкала могла сдвинуться, и подписи обязаны совпасть с картинкой.
  const legendDepth = depthOff ? 0 : state.depth
  // Шкала у каждого района своя — подписываем ту, что под центром кадра.
  const legendRegion = regionAt(regionScope.regions, state.lng, state.lat)

  // Отсчёт по клику. Вида здесь нет: раздел «Карты» показывает среду, а не
  // оценку, и пустая строка «Пригодность» читалась бы как поломка.
  const pointCard = usePointCard({ date: state.date, depth: legendDepth })
  const { data: legendBounds } = useQuery({
    queryKey: ['cabinet', 'legend', state.layer, state.date, legendDepth, tilesVersion, legendRegion],
    queryFn: () => cabinetApi.legend({
      layer: state.layer, date: state.date, depth: legendDepth, region: legendRegion,
    }),
    enabled: !!activeLayerSpec && activeLayerSpec.render === 'tiles',
  })

  // Шкала растра: та же, что печатается на плашке скачанного кадра. Слой без
  // палитры (виджет, геометрия) шкалы не даёт — `scaleView` отвечает null.
  const mapScale =
    activeLayerSpec && activeLayerSpec.render === 'tiles'
      ? {
          layerId: activeLayerSpec.id,
          unit: activeLayerSpec.unit,
          palette: activeLayerSpec.palette,
          min: legendBounds?.min ?? null,
          max: legendBounds?.max ?? null,
        }
      : null

  // Единый инструмент температуры (изотерма + градиенты) — общий хук, состояние в
  // ViewContext (блок «Наложения»), фикс 5–16 °C, над любым слоем. Температуру
  // берёт на глубине Контекста; сетку грузит по вьюпорту (viewKey).
  // Подпись вида в state уже округлена (roundView) — годится как ключ «сменился
  // вьюпорт»: пан/зум → пересчитать поле/плотность стрелок.
  const viewKey = `${state.lat}|${state.lng}|${state.zoom}`
  const { sstData, sstPartial, isothermDrawn, frontsDrawn, surfaceData, surfaceDrawn } =
    useTemperatureTools(mapRef, viewKey, loadNonce)
  // Оверлей «Волнение»: изо-линии высоты волны (над любым слоем). Стрелки течений
  // при этом ещё и краснеют (colorByWave ниже). Возвращает источник для легенды.
  const { source: waveSource, hasData: waveData, drawn: waveDrawn } =
    useWaveIso(mapRef, viewKey, loadNonce)

  // Палитра тайлов домена: у бренда со своей гаммой тайлы лежат отдельным
  // набором. Пусто — адрес в точности прежний.
  const palette = useTilePalette()

  // URL тайлов текущего базового слоя (батиметрия — статический шаблон без даты).
  const baseTileUrl = useCallback(
    () =>
      state.layer === 'bathymetry'
        ? bathymetryTileUrl()
        : sharedTileUrl(
            state.date, state.layer, depthOff ? 0 : state.depth, tilesVersion, palette,
          ),
    [state.layer, state.date, state.depth, depthOff, tilesVersion, palette],
  )
  currentTileUrlRef.current = baseTileUrl()

  // Запустить генерацию тайлов (prepare→confirm→pipeline) БЕЗ очистки текущего
  // слоя — чтобы серые плейсхолдеры оставались видны, пока считаются цветные.
  const startGeneration = useCallback((refresh = false) => {
    const map = mapRef.current
    if (!map || state.layer === 'bathymetry') return
    run({
      bbox: map.getBBox(),
      date: state.date,
      // Плюс данные под ВСЕ инструменты раздела (изотерма/фронты — SST,
      // волнение — поле волны и ветер), включены они сейчас или нет: иначе
      // кнопка качает слой раздела, а включённый следом инструмент остаётся без
      // данных и рисует пустоту (см. overlayLayers).
      layers: withSectionLayers([state.layer]),
      depth: depthOff ? 0 : state.depth,
      kind: 'raw',
      tileUrl: baseTileUrl(),
    }, { refresh })
  }, [state.layer, state.date, state.depth, depthOff, run, baseTileUrl])

  // смена слоя/глубины/даты сбрасывает отрисованный слой и прогресс-режим;
  // авторазрешение (useEffect ниже) заново решит, что показать для нового состояния.
  const patch = (p: Partial<MapState>) => {
    mapRef.current?.clearLayer()
    setLayerShown(false)
    setProgressive(false)
    setState(s => ({ ...s, ...p }))
  }

  // Пан/зум карты → сохранить центр+зум в state (→ URL). НЕ через patch (тот
  // сбрасывает слой); координаты не входят в deps autoResolve, так что перемещение
  // карты не перезапускает проверку охвата. Пропускаем no-op, чтобы не гонять URL.
  // Вид сериализуется как часть общего MapState страницы, но округление — общее.
  const handleMoveEnd = useCallback((v: { lng: number; lat: number; zoom: number }) => {
    const next = roundView(v)
    setState(s => (sameView(s, next) ? s : { ...s, ...next }))
  }, [])

  const handleLoad = () => {
    const map = mapRef.current
    if (!map) return
    map.clearLayer()
    setProgressive(false)
    if (state.layer === 'bathymetry') {
      map.showLayer(bathymetryTileUrl())
      setLayerShown(true)
      return
    }
    // «Обновить» (кнопка была ready) → пропустить модалку, сразу докачка+тайлы.
    startGeneration(loadState === 'ready')
  }

  // Авторазрешение состояния слоя при заходе/смене слоя/даты/глубины.
  //
  // Тайлы — быстрые статические PNG, а проверка охвата (availability → compute_delta)
  // тяжёлая (секунды). НЕ блокируем показ на ней: сразу оптимистично показываем
  // имеющиеся тайлы (если их нет — отрисуется прозрачно), затем availability уточняет
  // состояние в фоне:
  //   show-colored  → уже показано, ничего не делаем;
  //   grey-generate → данные есть, тайлов нет → запустить генерацию + докрашивание;
  //   wait-button   → ни тайлов, ни данных → убрать пустой слой, показать плашку.
  const autoResolve = useCallback(async () => {
    const map = mapRef.current
    if (!map) return
    const spec = catalog.find(l => l.id === state.layer)
    // Авто только для базовых raw-тайловых слоёв + батиметрии; оверлеи/модель — по кнопке/тумблеру.
    const auto =
      state.layer === 'bathymetry' ||
      (!!spec && spec.kind === 'raw' && !spec.overlay && spec.render === 'tiles')
    if (!auto) return

    // Оптимистичный показ — не ждём медленную проверку охвата.
    const urlAtStart = baseTileUrl()
    map.showLayer(urlAtStart)
    setLayerShown(true)
    setProgressive(false)

    // Батиметрия статична — охват проверять не нужно.
    if (state.layer === 'bathymetry') return

    let av
    try {
      av = await cabinetApi.availability({
        layer: state.layer,
        date: state.date,
        depth: depthOff ? 0 : state.depth,
        bbox: map.getBBox(),
      })
    } catch {
      return // сеть/ошибка — оставляем оптимистично показанный слой (кнопка доступна)
    }
    // Пользователь мог сменить слой/дату/глубину за время долгой проверки — не трогаем чужое.
    if (currentTileUrlRef.current !== urlAtStart) return

    // Версия готовых тайлов → в URL, иначе браузер отдаст старую копию из кэша.
    //
    // ВАЖНО: оптимистичный показ выше идёт по URL БЕЗ версии (её ещё не знаем),
    // то есть гарантированно попадает в кэш браузера со старой картинкой.
    // Поэтому, узнав версию, переприменяем слой — иначе ветка show-colored
    // («уже показано») навсегда оставила бы на экране устаревшие тайлы.
    const v = av.tiles_version ?? null
    setTilesVersion(v)
    if (state.layer !== 'bathymetry') {
      const versioned = sharedTileUrl(
        state.date, state.layer, depthOff ? 0 : state.depth, v, palette,
      )
      if (versioned !== urlAtStart) {
        map.refreshLayer(versioned)
        currentTileUrlRef.current = versioned
      }
    }

    const action = readinessAction(av)
    if (action === 'grey-generate') {
      setProgressive(true)
      startGeneration()
    } else if (action === 'wait-button') {
      map.clearLayer()
      setLayerShown(false)
    }
    // show-colored — уже показано оптимистично.
  }, [state.layer, state.date, state.depth, depthOff, catalog, baseTileUrl, startGeneration])

  // Триггерим авторазрешение при смене слоя/даты/глубины и когда подгрузился каталог.
  useEffect(() => {
    void autoResolve()
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [state.layer, state.date, state.depth, catalog.length])

  // Прогрессивное докрашивание: пока идёт генерация — периодически перезапрашиваем
  // тайлы (cache-buster), серые заглушки сменяются цветными. Стоп по tiles_ready.
  useEffect(() => {
    if (!progressive) return
    let alive = true
    let n = 0
    const tick = async () => {
      const map = mapRef.current
      if (!map || !alive) return
      n += 1
      map.refreshLayer(bustedTileUrl(baseTileUrl(), n))
      try {
        const av = await cabinetApi.availability({
          layer: state.layer,
          date: state.date,
          depth: depthOff ? 0 : state.depth,
          bbox: map.getBBox(),
        })
        if (alive && av.tiles_ready) {
          // Финальный цветной — по ВЕРСИОНИРОВАННОМУ URL: он стабилен (кэш на
          // сутки работает), но меняется при каждой перегенерации, поэтому
          // браузер не подсунет старую картинку.
          const v = av.tiles_version ?? null
          setTilesVersion(v)
          map.refreshLayer(
            sharedTileUrl(state.date, state.layer, depthOff ? 0 : state.depth, v, palette),
          )
          setProgressive(false)
        }
      } catch {
        /* ignore — следующий тик повторит */
      }
    }
    const id = setInterval(tick, 2500)
    return () => {
      alive = false
      clearInterval(id)
    }
  }, [progressive, state.layer, state.date, state.depth, depthOff, baseTileUrl])

  // Оверлеи (тайлы/землетрясения) и флот — общие для «Карт» и «Аналитики»:
  // рендер вынесен в общие хуки, состояние — в ViewContext (блок «Наложения»).
  const { earthquakeCount } = useOverlays(mapRef, ctxOverlays, ctxDate)
  // Землетрясения — общее наложение обоих разделов, и карточка у них общая.
  const quakeCard = useQuakeCard()
  // Карточка рубежа ИЭЗ: чей он и на каком основании. Без неё линию на карте
  // нечем назвать — вопрос «откуда она взялась» решался запросом к базе.
  const eezCard = useEezCard()
  useVesselFocus(mapRef, focusVessel, () => {})
  const { drawn: isobathsDrawn } = useIsobathTool(mapRef, isobathStep != null, isobathStep ?? 0, viewKey)
  // Рубежи ИЭЗ — оверлей с ВЬЮПОРТОМ (в отличие от землетрясений): линии
  // режутся и упрощаются по кадру, поэтому свой хук, а не useOverlays.
  const eezMeta = useEezLayer(mapRef, ctxOverlays.includes('eez'), viewKey)
  const districtsMeta = useDistrictsLayer(mapRef, ctxOverlays.includes('fishing_districts'), viewKey)
  // Тип дна по картам ГГК-1000: полигоны карты, а не узлы модели — проверка глазами.
  const substrateMeta = useSubstrateLayer(mapRef, ctxOverlays.includes('substrate'), viewKey)
  // Полигон карты дна под точкой отсчёта: берётся у НАРИСОВАННОГО слоя, отдельного
  // запроса к серверу не нужно — свойства лежат в самой фиче.
  const pointSubstrate = useMemo(() => {
    const map = mapRef.current?.getMap()
    const pick = pointCard.pick
    return map && pick ? substrateAt(map, pick.lng, pick.lat) : null
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [pointCard.pick, substrateMeta.classes])
  // Запретные районы — тот же тумблер, но со своей датой и видом: запрет либо
  // в силе для этого кадра, либо нет.
  const bansMeta = useBansLayer(mapRef, ctxOverlays.includes('fishing_districts'), viewKey,
    ctxDate, ctxSpecies)
  // Навигационные предупреждения NGA — в силе на дату карты.
  const navwarnMeta = useNavwarnLayer(mapRef, ctxOverlays.includes('navwarn'), viewKey, ctxDate)
  // Что нарисовали промрайоны и запреты и чья оговорка: в Омане районов нет,
  // а у запретов своя подпись — экран и кадр получают это одним объектом.
  const linesDrawn = {
    districts: districtsMeta.disclaimerKey != null,
    bans: bansMeta.drawn ?? undefined,
    bansDisclaimerKey: bansMeta.disclaimerKey,
    bansEverywhere: bansMeta.everywhere,
    substrate: substrateMeta.classes,
    navwarn: navwarnMeta.kinds,
  }
  useFleetLayer(mapRef, {
    fleet,
    species: ctxSpecies,
    showUncategorized,
    flags,
    onFlagCounts: setFlagCounts,
    viewKey,
    orgId: activeOrgId,
    refreshKey: fleetNonce,
  })

  // Стрелки направления течений поверх слоя «Течения» — как в разделе «Модели».
  // Цветной слой рисует только МОДУЛЬ скорости, без стрелок не видно, куда идёт
  // вода. Плотность/масштаб — от зума (arrows.ts): экранный шаг постоянный.
  // Источник — сырая таблица течений (эндпоинт currentsArrows), не тайлы: стрелки
  // появляются, как только есть поле на дату. Пересчёт debounce'ится (пан/зум).
  const [arrowsState, setArrowsState] = useState<ArrowsState>({ kind: 'ok' })
  useEffect(() => {
    const map = mapRef.current
    if (!map) return
    if (state.layer !== 'currents') {
      map.removeArrows('currents')
      setArrowsState({ kind: 'ok' })
      return
    }
    const zoom = state.zoom ?? 4
    let alive = true
    const id = setTimeout(() => {
      cabinetApi
        .currentsArrows({
          date: state.date,
          depth: depthOff ? 0 : state.depth,
          stepDeg: arrowStepDeg(zoom),
          bbox: map.getBBox(),
          waves: waveOverlay,
        })
        .then(fc => {
          if (!alive) return
          // Пусто (нет течений на дату / всё слабее порога) — снимаем слой, а не
          // оставляем стрелки прошлой даты поверх другой.
          if (!fc.features.length) {
            map.removeArrows('currents')
            setArrowsState({ kind: 'empty' })
            return
          }
          map.setArrows('currents', fc as GeoJSON.FeatureCollection, arrowScale(zoom), waveOverlay)
          setArrowsState({ kind: 'ok', truncated: fc.meta?.truncated })
        })
        .catch(err => {
          if (!alive) return
          // Молчать нельзя: стрелки прошлой даты под сегодняшней подписью — ложь.
          map.removeArrows('currents')
          setArrowsState({ kind: 'error', status: err?.status })
        })
    }, 250)
    return () => {
      alive = false
      clearTimeout(id)
    }
    // viewKey = центр+зум (пан/зум → пересчитать плотность); tilesVersion/layerShown
    // — чтобы после «Загрузить/Обновить» стрелки перезапросились на свежее поле;
    // waveOverlay — тумблер «Волнение» перекрашивает стрелки (нужен запрос wave_hs).
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [state.layer, state.date, state.depth, depthOff, viewKey, tilesVersion, layerShown, waveOverlay])

  const saveCurrentView = async () => {
    const name = window.prompt(t('maps.savedView.prompt'))
    if (!name) return
    await cabinetApi.createSavedView(name, saveViewState(
      'maps',
      // Флота нет в MapState (общий стор «Наложения») — кладём отдельным ключом,
      // чтобы сохранённый вид переносил и наложения флота.
      { ...state, fleet: serializeFleet(fleet) },
      mapRef.current?.getBBox(),
      today(),
    ))
  }

  // Инструменты раздела «Карты» — единый набор, отдаётся в общий рельс.
  // Что писать на плашке скачанной картинки (слой/дата/глубина/вид).
  const exportInfo = useExportInfo(layerLabel(state.layer), {
    scale: mapScale,
    earthquakes: earthquakeCount,
    waveSource,
    sstData,
    sstPartial,
    waveData,
    surfaceData,
    drawn: { isotherm: isothermDrawn, isothermSurface: surfaceDrawn,
             fronts: frontsDrawn, isobaths: isobathsDrawn, waves: waveDrawn,
             ...linesDrawn },
  })

  const toolsInner = (
    <div className="space-y-6">
      <LayerPicker layers={catalog} active={state.layer} onSelect={id => patch({ layer: id })} />

      {/* Дата/глубина → блок «Контекст»; оверлеи и инструмент температуры
          (изотермы + градиенты) → блок «Наложения» над разделами. */}

      {/* Скачать текущий вид: PNG кадра. Плашка в углу картинки наследует
          глубину из ползунка сайдбара. Анимации в разделе нет — GIF кнопка не
          предлагает (усики живут на «Моделях», в слое течений). */}
      <ExportViewButton
        mapRef={mapRef}
        info={exportInfo}
        // Кадр без данных наружу не отдаём. Слой снят с карты («Слой не
        // загружен») — значит поля под ним нет; идёт докрашивание — значит
        // тайлы ещё считаются, и в кадр попали бы серые заглушки.
        layerState={layerStateOf(layerShown, progressive)}
      />

      <button
        type="button"
        onClick={saveCurrentView}
        className="flex w-full items-center justify-center gap-2 rounded-lg border border-hull px-3 py-2 text-sm text-mist transition-colors hover:border-sonar hover:text-foam"
      >
        <Icon name="ic-save-view" className="h-4 w-4" /> {t('maps.savedView.save')}
      </button>
    </div>
  )

  return (
    <div className="relative h-full w-full">
      {/* Инструменты раздела — в общий рельс (портал); при свёрнутом рельсе — плавающей картой */}
      {railSlot ? (
        createPortal(
          <div className="space-y-6 pb-2 pt-1">{toolsInner}</div>,
          railSlot,
        )
      ) : (
        <MapPanel storageKey="maps.panel" className="w-64" surface="bg-deep">
          {/* Свёрнутый рельс: блоки «Контекст» и «Наложения» — плавающей карточкой. */}
          <div className="px-3 pb-3">
            <ContextBlock />
            <OverlaysBlock />
            {toolsInner}
          </div>
        </MapPanel>
      )}

      {/* Вид входа: ссылка и память раздела сильнее рамки района, а пока набор
          районов не приехал — карту не строим вовсе. Построить на зашитом
          центре и перелететь значило бы показать на домене партнёра кадр
          чужого океана (см. `viewToOpen`). */}
      {openWith && (
        <MapCanvas
          ref={mapRef}
          center={
            openWith.lng != null && openWith.lat != null
              ? [openWith.lng, openWith.lat]
              : undefined
          }
          zoom={openWith.zoom ?? undefined}
          onPointClick={pointCard.open}
          onEezHover={eezCard.handleHover}
          onEezClick={eezCard.handleClick}
          onQuakeHover={quakeCard.handleHover}
          onQuakeClick={quakeCard.handleClick}
          onMoveEnd={handleMoveEnd}
        />
      )}
      {/* Отсчёт: карточка встаёт у клика и держится, пока не закроют. */}
      {pointCard.pick && (
        <PointCard
          anchor={pointCard.pick}
          probe={pointCard.probe}
          loading={pointCard.loading}
          failed={pointCard.failed}
          onClose={pointCard.close}
          substrate={pointSubstrate}
        />
      )}
      {/* Рубеж ИЭЗ: читалка по наведению, закреплённая по клику. Без неё
          линию на карте нечем назвать — чей это рубеж и на каком основании
          он проведён, приходилось спрашивать у базы. */}
      {eezCard.shown && (
        <EezCard
          anchor={eezCard.shown.hit}
          props={eezCard.shown.hit.props}
          pinned={eezCard.shown.pinned}
          onClose={eezCard.close}
        />
      )}
      {/* Землетрясение: читалка по наведению, закреплённая по клику. */}
      {quakeCard.shown && (
        <EarthquakeCard
          anchor={quakeCard.shown.hit}
          props={quakeCard.shown.hit.props}
          pinned={quakeCard.shown.pinned}
          onClose={quakeCard.close}
        />
      )}
      {regionScope.forbidden && <RegionForbiddenPlate />}

      {/* Пустое состояние — до первой загрузки (карта-подложка светлая → карточка-бэкинг) */}
      {!layerShown && loadState !== 'downloading' && loadState !== 'preparing' && (
        <div className="pointer-events-none absolute left-1/2 top-[42%] -translate-x-1/2 -translate-y-1/2 rounded-xl border border-hull bg-abyss/85 px-5 py-4 text-center shadow-console backdrop-blur">
          <Icon name="ic-layers" className="mx-auto mb-2 h-9 w-9 text-mist" />
          <div className="text-sm font-semibold text-foam">{t('maps.notLoaded.title')}</div>
          <div className="mt-1 text-xs text-mist">{t('maps.notLoaded.hint')}</div>
        </div>
      )}

      <div className="pointer-events-none absolute inset-0">
        <div className="absolute bottom-6 left-1/2 -translate-x-1/2">
          <div className="pointer-events-auto">
            <LoadDataButton
              state={loadState}
              progress={progress}
              cardProgress={cardProgress}
              error={error}
              onClick={handleLoad}
            />
          </div>
        </div>
        {/* Левый нижний угол: легенда, под ней — короткая ссылка на этот вид. */}
        <div className="absolute bottom-6 left-4 flex flex-col items-start gap-2">
          <ScalePanel scale={mapScale} />
          <ShareLinkButton />
        </div>
        {/* Единая легенда активных наложений — одна плашка справа снизу. */}
        <div className="absolute bottom-24 right-4">
          <MapLegendPanel
              earthquakes={earthquakeCount}
              waveSource={waveSource}
              sstData={sstData}
              sstPartial={sstPartial}
              waveData={waveData}
              eezAttribution={eezMeta.attribution}
              surfaceData={surfaceData}
              drawn={{ isotherm: isothermDrawn, isothermSurface: surfaceDrawn,
                       fronts: frontsDrawn, isobaths: isobathsDrawn, waves: waveDrawn,
             ...linesDrawn }}
            />
        </div>
        {/* Течения: стрелки честно сообщают о пустоте/сбое, а не молча пропадают */}
        {state.layer === 'currents' && arrowsNotice(arrowsState) && (
          <div className="absolute bottom-20 left-1/2 -translate-x-1/2 rounded-lg border border-hull bg-abyss/90 px-3 py-1.5 text-xs text-mist shadow-console backdrop-blur">
            {arrowsNotice(arrowsState)}
          </div>
        )}
      </div>

      {loadState === 'manifest' && manifest && (
        <ManifestModal manifest={manifest} onConfirm={confirm} onCancel={cancel} />
      )}
    </div>
  )
}
